import React from 'react';

import { MenuCarouselItem, MenuCarouselItemProps, RewardsChip } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { ISanityImage } from '@rbi-ctg/menu';
import { IStore } from '@rbi-ctg/store';
import Picture from 'components/picture';
import {
  makeLoyaltyRewardDetailsPath,
  makeUrlForIncentive,
} from 'pages/loyalty/loyalty-incentives-components/incentive-details/utils';
import { useLocale } from 'state/intl';
import { useLogger } from 'state/logger';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { useMenuContext } from 'state/menu';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';

import { IRewardsCarouselCardProps } from './types';

export const RewardsCarouselCardV2 = ({
  itemOffset,
  lastItemSlide,
  reward,
  index,
}: IRewardsCarouselCardProps &
  Pick<MenuCarouselItemProps, 'itemOffset' | 'lastItemSlide'> & { index: number }) => {
  const logger = useLogger();
  const { noStoreSelected, store } = useStoreContext();
  const { setSelectedStaticMenuItem } = useMenuContext();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const incentiveRef = React.useRef(
    reward.incentives?.find(val => val?._id === reward.rewardBenefitId)
  );
  const { language } = useLocale();
  const { serviceMode } = useServiceModeContext();
  const { isInRestaurantRedemptionEnabledAtStore } = useInRestaurantRedemptionContext();

  const sanityImage = reward.image?.locale as ISanityImage;
  const imageJSX = sanityImage && (
    <Picture objectFitContain image={sanityImage} alt={reward.imageDescription?.locale ?? ''} />
  );

  const handleRewardClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      async function navigateToReward() {
        const rewardWizardUrl = makeUrlForIncentive(reward);
        if (!rewardWizardUrl) {
          logger.warn(
            `Attempting to make a url for reward that is missing data: reward ${reward._id}`
          );
          return;
        }
        const indexOfSearch = rewardWizardUrl.indexOf('?');
        const search = indexOfSearch !== -1 ? rewardWizardUrl.slice(indexOfSearch) : '';
        if (incentiveRef.current && noStoreSelected) {
          setSelectedStaticMenuItem({
            itemId: `${incentiveRef.current._type}-${incentiveRef.current._id}`,
            itemName: reward.name?.locale || '',
            search,
          });
        }

        const shouldNavigateToRewardsDetails =
          reward._id &&
          (await isInRestaurantRedemptionEnabledAtStore(store as IStore, serviceMode));

        if (shouldNavigateToRewardsDetails) {
          navigate(makeLoyaltyRewardDetailsPath({ rewardId: reward._id, search, formatMessage }));
        } else {
          navigate(rewardWizardUrl);
        }
      }
      navigateToReward();
    },
    [
      formatMessage,
      isInRestaurantRedemptionEnabledAtStore,
      logger,
      navigate,
      noStoreSelected,
      reward,
      serviceMode,
      setSelectedStaticMenuItem,
      store,
    ]
  );

  if (!reward.incentives?.length) {
    return null;
  }

  return (
    <MenuCarouselItem
      itemOffset={itemOffset}
      lastItemSlide={lastItemSlide}
      onClick={handleRewardClick}
      aria-label={'aria'}
      header={reward.name?.[language] || ''}
      image={imageJSX}
      imageAlt={reward.imageDescription?.locale || ''}
      index={index}
      key={`reward-${index}`}
      variant="rewards"
      rewardsChip={
        <RewardsChip
          rewardsLocked={reward.locked}
          aria-label={`Get with ${reward.pointCost} reward points`}
        >
          {String(reward.pointCost || '')}
        </RewardsChip>
      }
    />
  );
};
